<template>
  <div class="api-settings-pane">
    <el-row :gutter="20" class="mb-2">
      <el-col :span="8">
        <el-form-item :label="$t('reportViewName')">
          <el-input
            :model-value="settings.reportViewName"
            size="small"
            @input="updateField('reportViewName', $event)"
            @blur="trimField('reportViewName')"
          ></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item :label="$t('reportName')">
          <el-input
            :model-value="settings.reportName"
            size="small"
            @input="updateField('reportName', $event)"
            @blur="trimField('reportName')"
          ></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item :label="$t('account')" class="mb-0">
          <el-select
            :model-value="settings.account"
            size="small"
            filterable
            @change="updateField('account', $event)"
          >
            <el-option
              v-for="item in EtradeRequestBodyAccountOptions"
              :key="item.label"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="mb-2">
      <el-col :span="12">
        <el-form-item :label="$t('beginDate')">
          <div class="date-selector">
            <el-select
              :model-value="settings.beginDateType"
              size="small"
              @change="updateField('beginDateType', $event)"
            >
              <el-option
                v-for="opt in dateTypeOptions"
                :key="opt.value"
                :label="$t(opt.label)"
                :value="opt.value"
              ></el-option>
            </el-select>
            <el-date-picker
              v-if="settings.beginDateType === 'specific'"
              :model-value="settings.beginDate"
              type="date"
              :placeholder="$t('selectDate')"
              size="small"
              format="MM-DD-YYYY"
              value-format="MM-DD-YYYY"
              @update:model-value="updateField('beginDate', $event)"
            ></el-date-picker>
          </div>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item :label="$t('endDate')">
          <div class="date-selector">
            <el-select
              :model-value="settings.endDateType"
              size="small"
              @change="updateField('endDateType', $event)"
            >
              <el-option
                v-for="opt in dateTypeOptions"
                :key="opt.value"
                :label="$t(opt.label)"
                :value="opt.value"
              ></el-option>
            </el-select>
            <el-date-picker
              v-if="settings.endDateType === 'specific'"
              :model-value="settings.endDate"
              type="date"
              :placeholder="$t('selectDate')"
              size="small"
              format="MM-DD-YYYY"
              value-format="MM-DD-YYYY"
              @update:model-value="updateField('endDate', $event)"
            ></el-date-picker>
          </div>
        </el-form-item>
      </el-col>
    </el-row>

    <h3 class="mt-2 mb-1">{{ $t('filters') }}</h3>
    <div
      v-for="(filter, filterIndex) in settings.filters"
      :key="filterIndex"
      class="filter-row d-flex align-items-center"
    >
      <el-form-item :label="$t('name')" class="filter-field-name">
        <el-input
          v-model="filter.name"
          size="small"
          @blur="
            filter.name = filter.name ? filter.name.trim() : '';
            $emit('change');
          "
          @change="$emit('change')"
        ></el-input>
      </el-form-item>
      <el-form-item :label="$t('operator')" class="filter-field-operator">
        <el-input
          v-model="filter.operator"
          size="small"
          @blur="
            filter.operator = filter.operator ? filter.operator.trim() : '';
            $emit('change');
          "
          @change="$emit('change')"
        ></el-input>
      </el-form-item>
      <el-form-item :label="$t('values')" class="filter-field-values">
        <el-input
          v-model="filter.values"
          size="small"
          :placeholder="$t('enterValuesSeparatedBySemicolons')"
          @blur="
            filter.values = filter.values ? filter.values.trim() : '';
            $emit('change');
          "
          @change="$emit('change')"
        />
      </el-form-item>
      <el-button
        type="default"
        size="small"
        class="mt-1"
        @click="$emit('delete-filter', filterIndex)"
      >
        {{ $t('remove') }}
      </el-button>
    </div>
    <el-button type="primary" size="small" @click="$emit('add-filter')">
      {{ $t('addFilter') }}
    </el-button>
  </div>
</template>

<script lang="ts">
import {
  ElButton,
  ElCol,
  ElDatePicker,
  ElFormItem,
  ElInput,
  ElOption,
  ElRow,
  ElSelect
} from 'element-plus';
import {
  EtradeApiRequestBodyTemplate,
  EtradeRequestBodyAccountOptions,
  dateTypeOptions
} from '../models/EtradeApiModels';

export default {
  name: 'ApiRequestBodySettings',
  components: {
    ElButton,
    ElCol,
    ElDatePicker,
    ElFormItem,
    ElInput,
    ElOption,
    ElRow,
    ElSelect
  },
  props: {
    settings: {
      type: Object as () => EtradeApiRequestBodyTemplate,
      required: true
    }
  },
  emits: ['change', 'add-filter', 'delete-filter', 'update-field'],
  setup(props, { emit }) {
    const updateField = (field: string, value: any) => {
      emit('update-field', field, value);
    };

    const trimField = (field: string) => {
      const currentValue = props.settings[field];
      if (currentValue) {
        const trimmedValue = currentValue.trim();
        if (trimmedValue !== currentValue) {
          emit('update-field', field, trimmedValue);
        }
      }
    };

    return {
      EtradeRequestBodyAccountOptions,
      dateTypeOptions: dateTypeOptions(),
      updateField,
      trimField
    };
  }
};
</script>

<style lang="scss" scoped>
.api-settings-pane {
  padding: 15px;
  border-radius: 6px;
  position: relative;
}

.date-selector {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
}

.filter-row {
  display: flex;
  gap: 15px;
  align-items: flex-center;
}

.filter-field-name {
  flex: 1;
  min-width: 150px;
}

.filter-field-operator {
  flex: 0 0 150px;
  min-width: 150px;
}

.filter-field-values {
  flex: 2;
  min-width: 250px;
}
</style>
